import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StitchLayout from '../../components/Crm/CrmLayout';
import { apis } from '../../../core/services/params';
import axiosInstance from '../../../core/services/axiosConfig';
import { Button, Input, Segmented } from 'antd';
import { PhoneOutlined, MailOutlined, IdcardOutlined, SearchOutlined } from '@ant-design/icons';
import { TICKET_STATUS_MAP, PRIORITY_MAP } from './crmConstants';


// User data shape from fetch-user API
interface UserData {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    phoneCode: string | null;
    userType: string;
    signupSource: string;
    active: boolean;
    emailVerified: number;
    phoneVerified: number;
    codEnabled: boolean;
    customerCategory: number;
    isAgent: number;
    languageCode: string;
}

// Order item shape
interface OrderItem {
    item_id: number;
    product_image_url: string;
    product_name: string;
    status: string;
}

// Order shape from fetch-user-orders API
interface Order {
    order_id: number;
    order_date: string;
    order_value: number;
    status: string | null;
    items: OrderItem[];
}

// Status badge colour helper
const getStatusStyle = (status: string | null) => {
    const s = (status || '').toUpperCase();
    if (s === 'DELIVERED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (s === 'CANCELLED') return 'bg-red-100 text-red-600 border-red-200';
    if (s.includes('PAYMENT FAILED')) return 'bg-orange-100 text-orange-600 border-orange-200';
    if (s === 'PROCESSING') return 'bg-blue-100 text-blue-600 border-blue-200';
    if (s === 'SHIPPED') return 'bg-purple-100 text-purple-600 border-purple-200';
    return 'bg-gray-100 text-gray-500 border-gray-200';
};

// Pill badge helper
const Pill = ({ label, color }: { label: string; color: string }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${color}`}>
        {label}
    </span>
);

// CSS class map for ticket status badge — covers all known Freshdesk status codes
const TICKET_STATUS_CSS: Record<number, { label: string; color: string }> = {
    2: { label: 'Open', color: 'bg-amber-100 text-amber-700' },
    3: { label: 'Pending', color: 'bg-orange-100 text-orange-600' },
    4: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700' },
    5: { label: 'Closed', color: 'bg-gray-100 text-gray-500' },
    11: { label: 'Chatbot - Resolved', color: 'bg-cyan-100 text-cyan-700' },
    12: { label: 'Responded', color: 'bg-blue-100 text-blue-700' },
    13: { label: 'Pending by Customer', color: 'bg-yellow-100 text-yellow-700' },
    14: { label: 'Pending by Moglix', color: 'bg-red-100 text-red-600' },
    15: { label: 'Resolved Email', color: 'bg-lime-100 text-lime-700' },
};

// Returns label+CSS class for any ticket status code
const ticketStatus = (status: number) =>
    TICKET_STATUS_CSS[status] ||
    { label: TICKET_STATUS_MAP[status] || `Status ${status}`, color: 'bg-gray-100 text-gray-500' };

// CSS class map for ticket priority badge — covers all known Freshdesk priority codes
const PRIORITY_CSS: Record<number, { label: string; color: string }> = {
    1: { label: 'Low', color: 'text-gray-400' },
    2: { label: 'Medium', color: 'text-blue-500' },
    3: { label: 'High', color: 'text-orange-500' },
    4: { label: 'Urgent', color: 'text-red-500' },
};

// Returns label+CSS class for any ticket priority code
const ticketPriority = (priority: number) =>
    PRIORITY_CSS[priority] ||
    { label: PRIORITY_MAP[priority] || `Priority ${priority}`, color: 'text-gray-400' };

// Source icon for ticket channel
const sourceIcon = (source: number) => {
    if (source === 1) return '📧';
    if (source === 2) return '🌐';
    if (source === 3) return '📞';
    return '💬';
};

// Format seconds to mm:ss string
const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

// Load recent searches from localStorage
const loadRecent = (): any[] => {
    try { return JSON.parse(localStorage.getItem('stitch_recent_searches') || '[]'); } catch { return []; }
};

// Save a search entry to localStorage
const saveRecent = (userId: string, label: string, searchType: string, searchValue: string) => {
    const prev = loadRecent();
    const updated = [
        { userId, label, value: searchValue, type: searchType, timestamp: Date.now() },
        ...prev.filter((s: any) => s.userId !== userId),
    ].slice(0, 10);
    localStorage.setItem('stitch_recent_searches', JSON.stringify(updated));
};

const PER_PAGE = 10;

const CustomerProfile = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    // Search bar state - shared across both empty and loaded states
    const [searchType, setSearchType] = useState<'Mobile' | 'Email' | 'ID'>('Mobile');
    const [searchValue, setSearchValue] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [recentSearches, setRecentSearches] = useState<any[]>(loadRecent);
    const [searchExpanded, setSearchExpanded] = useState(!id);
    const inputRef = useRef<HTMLInputElement>(null);

    // Customer data state
    const [user, setUser] = useState<UserData | null>(null);
    const [orders, setOrders] = useState<Order[]>([]);
    const [tickets, setTickets] = useState<any[]>([]);
    const [ivrCalls, setIvrCalls] = useState<any[]>([]);
    const [aiCalls, setAiCalls] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'orders' | 'tickets' | 'ivr' | 'ai'>('orders');
    const [loadingUser, setLoadingUser] = useState(false);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [loadingIvr, setLoadingIvr] = useState(false);
    const [loadingAi, setLoadingAi] = useState(false);
    const [userError, setUserError] = useState<string | null>(null);
    const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
    const [ivrPage, setIvrPage] = useState(1);
    const [ivrTotal, setIvrTotal] = useState(0);
    const [aiPage, setAiPage] = useState(1);
    const [aiTotal, setAiTotal] = useState(0);

    // Agent notes state — list, new note input, save/edit loading, edit tracking
    const [notes, setNotes] = useState<any[]>([]);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingNoteText, setEditingNoteText] = useState('');
    const [updatingNoteId, setUpdatingNoteId] = useState<string | null>(null);
    const [priorityStatus, setPriorityStatus] = useState<number | false | null>(null);
    const [loadingPriority, setLoadingPriority] = useState(false);

    // Reset all profile data when navigating to a new customer
    useEffect(() => {
        if (!id) {
            setUser(null);
            setOrders([]);
            setTickets([]);
            setIvrCalls([]);
            setAiCalls([]);
            setSearchExpanded(true);
            setPriorityStatus(null);
            return;
        }
        setSearchExpanded(false);
        setUserError(null);
        setActiveTab('orders');
        setExpandedOrder(null);
        setPriorityStatus(null);
    }, [id]);

    // Fetch real user details from API
    useEffect(() => {
        if (!id) return;
        setLoadingUser(true);
        axiosInstance.get(`${apis.FETCH_USER}?user_id=${id}`)
            .then(response => {
                const json = response.data;
                if (json.status && json.data) setUser(json.data);
                else setUserError('User not found.');
            })
            .catch(() => setUserError('Failed to load user.'))
            .finally(() => setLoadingUser(false));
    }, [id]);

    // Fetch real order history from API
    useEffect(() => {
        if (!id) return;
        setLoadingOrders(true);
        axiosInstance.get(`${apis.FETCH_USER_ORDERS}?user_id=${id}`)
            .then(response => {
                const json = response.data;
                if (json.status && json.data) setOrders(json.data);
            })
            .catch(() => { })
            .finally(() => setLoadingOrders(false));
    }, [id]);

    // Fetch agent notes for this customer whenever the customer ID changes
    useEffect(() => {
        if (!id) { setNotes([]); return; }
        setLoadingNotes(true);
        axiosInstance.get(`${apis.AGENT_NOTES_GET}?customer_id=${id}`)
            .then(response => {
                // Handle any response shape: root array, or nested under .notes / .data
                const raw = response.data;
                const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.notes) ? raw.notes : (Array.isArray(raw?.data) ? raw.data : []));
                setNotes(arr);
            })
            .catch(() => { })
            .finally(() => setLoadingNotes(false));
    }, [id]);

    // Eagerly fetch freshdesk tickets as soon as customer id is available
    useEffect(() => {
        if (!id) return;
        setLoadingTickets(true);
        axiosInstance.post(`${apis.FRESHDESK_TICKETS_LIST}?page=1&per_page=50`, {
            filters: { requester_userid: Number(id) }
        })
            .then(response => {
                const json = response.data;
                setTickets(json.data || []);
            })
            .catch(() => { })
            .finally(() => setLoadingTickets(false));
    }, [id]);

    // Eagerly fetch IVR call history only when a valid phone is present
    useEffect(() => {
        if (!id || !user) return;
        if (!user.phone) { setLoadingIvr(false); setIvrCalls([]); setIvrTotal(0); return; }
        setLoadingIvr(true);
        const e164 = `+91${user.phone.replace(/^\+91/, '')}`;
        axiosInstance.post(`${apis.OZONETEL_LIVE_REPORT}?page=${ivrPage}&per_page=${PER_PAGE}`, {
            filters: { UCID: '', Agent: '', AgentStatus: '', E164: e164, CampaignName: '', Status: '' }
        })
            .then(response => {
                const json = response.data;
                setIvrCalls(json.data || []);
                setIvrTotal(json.pagination?.total || 0);
            })
            .catch(() => { })
            .finally(() => setLoadingIvr(false));
    }, [id, user, ivrPage]);

    useEffect(() => {
        if (!user?.id) return;
        setLoadingPriority(true);
        axiosInstance.post(apis.CHECK_CUSTOMER_PRIORITY_ORDERS, {
            orderId: [], pageNumber: 1, pageCount: 50, itemId: [], itemStatus: [],
            supplierId: [], customerId: [user.id], email: '', phone: '', awb: [],
            forwardAwb: [], shipperId: [], returnId: [], productRef: '', shipmentId: [],
            clusterList: [], categoryList: [], countryCode: 'IN',
        })
            // If data array is empty, customer is not a priority customer
            .then(res => {
                const data: any[] = res.data?.data || [];
                if (data.length === 0) {
                    setPriorityStatus(false);
                } else {
                    // Use highest priority status found (0 is highest)
                    const minStatus = Math.min(...data.map((d: any) => d.priorityStatus));
                    setPriorityStatus(minStatus);
                }
            })
            .catch(() => setPriorityStatus(false))
            .finally(() => setLoadingPriority(false));
    }, [user?.id]);

    // Eagerly fetch AI Voice Agent calls only when a valid phone is present
    useEffect(() => {
        if (!id || !user) return;
        if (!user.phone) { setLoadingAi(false); setAiCalls([]); setAiTotal(0); return; }
        setLoadingAi(true);
        const phone = user.phone.replace(/^\+91/, '');
        axiosInstance.post(`${apis.OZONETEL_VOICE_AI_AGENT}?page=${aiPage}&per_page=${PER_PAGE}`, {
            filters: { caller_phone: phone }
        })
            .then(response => {
                const json = response.data;
                setAiCalls(json.data || []);
                setAiTotal(json.pagination?.total || 0);
            })
            .catch(() => { })
            .finally(() => setLoadingAi(false));
    }, [id, user, aiPage]);

    // Handle customer search and navigate to profile
    const handleSearch = async () => {
        if (!searchValue.trim()) { setSearchError('Please enter a search value'); return; }
        setSearchLoading(true);
        setSearchError(null);
        try {
            const params: any = {};
            if (searchType === 'ID') params.user_id = searchValue;
            if (searchType === 'Mobile') params.phone = searchValue;
            if (searchType === 'Email') params.email = searchValue;
            const response = await axiosInstance.get(apis.FETCH_USER, { params });
            if (response.data?.status && response.data?.data) {
                const userData = response.data.data;
                saveRecent(String(userData.id), userData.firstName || searchValue, searchType, searchValue);
                setRecentSearches(loadRecent());
                setSearchValue('');
                navigate(`/crm/customer/${userData.id}`);
            } else {
                setSearchError('User not found');
            }
        } catch {
            setSearchError('Failed to fetch user. Please try again.');
        } finally {
            setSearchLoading(false);
        }
    };

    // Create a new agent note via POST API
    const handleCreateNote = async () => {
        if (!newNote.trim() || !id) return;
        setSavingNote(true);
        try {
            const agentEmail = (window as any).__userEmail || localStorage.getItem('user_email') || 'agent@moglix.com';
            await axiosInstance.post(apis.AGENT_NOTES_CREATE, {
                agent_email: agentEmail,
                customer_id: id,
                note: newNote.trim(),
                timestamp: new Date().toISOString(),
            });
            // Re-fetch notes after create to get server-assigned ID and all fields
            const refreshed = await axiosInstance.get(`${apis.AGENT_NOTES_GET}?customer_id=${id}`);
            const raw = refreshed.data;
            // Parse re-fetched notes using same multi-shape handler
            setNotes(Array.isArray(raw) ? raw : (Array.isArray(raw?.notes) ? raw.notes : (Array.isArray(raw?.data) ? raw.data : [])));
            setNewNote('');
        } catch { }
        finally { setSavingNote(false); }
    };

    // Update an existing note — same endpoint as create but with note_id to target the record
    const handleUpdateNote = async (noteId: string) => {
        if (!editingNoteText.trim()) return;
        setUpdatingNoteId(noteId);
        try {
            const agentEmail = (window as any).__userEmail || localStorage.getItem('user_email') || 'agent@moglix.com';
            await axiosInstance.post(apis.AGENT_NOTES_UPDATE, {
                note_id: noteId,
                agent_email: agentEmail,
                customer_id: id,
                note: editingNoteText.trim(),
                timestamp: new Date().toISOString(),
            });
            // Re-fetch notes after update to reflect server state
            const refreshed = await axiosInstance.get(`${apis.AGENT_NOTES_GET}?customer_id=${id}`);
            const raw = refreshed.data;
            setNotes(Array.isArray(raw) ? raw : (Array.isArray(raw?.notes) ? raw.notes : (Array.isArray(raw?.data) ? raw.data : [])));
            setEditingNoteId(null);
            setEditingNoteText('');
        } catch { }
        finally { setUpdatingNoteId(null); }
    };

    // Derived order stats — totalSpend counts only DELIVERED orders
    const totalSpend = orders
        .filter(o => (o.status || '').toUpperCase() === 'DELIVERED')
        .reduce((s, o) => s + (o.order_value || 0), 0);
    const deliveredCount = orders.filter(o => (o.status || '').toUpperCase() === 'DELIVERED').length;
    // AOV = total delivered spend divided by number of delivered orders
    const aov = deliveredCount > 0 ? totalSpend / deliveredCount : 0;
    const lastOrder = orders[0];
    const fullName = user ? `${user.firstName}`.trim() : '—';
    const initials = user ? `${user.firstName?.[0] || ''}`.toUpperCase() : '?';

    // Tab config with live counts from eagerly loaded data
    const tabs = [
        { key: 'orders', label: `Order History (${orders.length})` },
        { key: 'tickets', label: `Tickets (${tickets.length})` },
        { key: 'ivr', label: `IVR Calls (${ivrTotal || ivrCalls.length})` },
        { key: 'ai', label: `🤖 AI Voice (${aiTotal || aiCalls.length})` },
    ] as const;

    return (
        <StitchLayout>
            <div className="space-y-4">

                {/* Unified search bar - compact when profile is loaded, expanded when no customer */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    {/* Search header row with toggle */}
                    <div
                        className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => { if (id) setSearchExpanded(v => !v); }}
                    >
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            {id && user && !searchExpanded ? (
                                <p className="text-sm text-gray-500">
                                    Viewing <span className="font-semibold text-gray-800">{fullName}</span>
                                    <span className="text-gray-400 ml-2">· Click to search another customer</span>
                                </p>
                            ) : (
                                <p className="text-sm font-semibold text-gray-700">Customer Search</p>
                            )}
                        </div>
                        {id && (
                            <span className="text-gray-300 text-xs transition-transform" style={{ transform: searchExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                        )}
                    </div>

                    {/* Collapsible search form using Interaction Timeline style */}
                    {searchExpanded && (
                        <div className="border-t border-gray-50 px-5 pb-5 pt-4">
                            {/* Segmented type selector matching Interaction Timeline */}
                            <div className="flex flex-col gap-2 mb-4">
                                <Segmented
                                    value={searchType}
                                    onChange={(val) => { setSearchType(val as any); setSearchError(null); setSearchValue(''); }}
                                    options={[
                                        { label: <span className="flex items-center gap-1 px-1"><PhoneOutlined />Mobile</span>, value: 'Mobile' },
                                        { label: <span className="flex items-center gap-1 px-1"><MailOutlined />Email</span>, value: 'Email' },
                                        { label: <span className="flex items-center gap-1 px-1"><IdcardOutlined />ID</span>, value: 'ID' },
                                    ]}
                                    className="self-start"
                                />
                                {/* Input and search button row */}
                                <div className="flex gap-2">
                                    <Input
                                        ref={inputRef as any}
                                        size="middle"
                                        prefix={
                                            searchType === 'Mobile' ? <PhoneOutlined className="text-gray-400" /> :
                                                searchType === 'Email' ? <MailOutlined className="text-gray-400" /> :
                                                    <IdcardOutlined className="text-gray-400" />
                                        }
                                        placeholder={`Enter Customer ${searchType}`}
                                        value={searchValue}
                                        onChange={e => { setSearchValue(e.target.value); setSearchError(null); }}
                                        onPressEnter={handleSearch}
                                        allowClear
                                        autoFocus
                                        status={searchError ? 'error' : undefined}
                                        className="w-56"
                                    />
                                    <Button size="middle" type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={searchLoading}>
                                        Search
                                    </Button>
                                </div>
                                {/* Inline error message below input */}
                                {searchError && <p className="text-red-500 text-xs mt-0.5">{searchError}</p>}
                            </div>

                            {/* Recent searches as compact chip pills in a horizontal wrap */}
                            {recentSearches.length > 0 && (
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Recent</p>
                                        <button onClick={() => { localStorage.removeItem('stitch_recent_searches'); setRecentSearches([]); }}
                                            className="text-[10px] text-red-400 hover:text-red-600 transition-colors">Clear</button>
                                    </div>
                                    {/* Chip list — wraps naturally, glows blue on hover */}
                                    <div className="flex flex-wrap gap-2">
                                        {recentSearches.slice(0, 10).map((s: any) => {
                                            const typeColor =
                                                s.type === 'Mobile' ? 'bg-blue-100 text-blue-600' :
                                                    s.type === 'Email' ? 'bg-violet-100 text-violet-600' :
                                                        'bg-amber-100 text-amber-600';
                                            const typeLabel = s.type === 'Mobile' ? 'M' : s.type === 'Email' ? 'E' : 'ID';
                                            return (
                                                <button
                                                    key={s.userId}
                                                    onClick={() => navigate(`/crm/customer/${s.userId}`)}
                                                    className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-full text-sm transition-all group shadow-none hover:shadow-sm"
                                                >
                                                    {/* Type badge circle */}
                                                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${typeColor}`}>
                                                        {typeLabel}
                                                    </span>
                                                    <span className="font-medium text-gray-700 group-hover:text-blue-700 text-xs transition-colors">
                                                        {s.label || s.value}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Show full page empty state when no customer is selected */}
                {!id && recentSearches.length === 0 && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-3xl mx-auto mb-4">🔍</div>
                        <h2 className="text-lg font-bold text-gray-700 mb-1">Search for a Customer</h2>
                        <p className="text-sm text-gray-400">Enter a mobile number, email, or customer ID above to view their profile.</p>
                    </div>
                )}

                {/* Customer profile sections — only shown when an ID is present */}
                {id && (
                    <>
                        {/* ── Compact unified profile banner ─────────────────────────────── */}
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-3">
                            {/* Row 1: Identity left · KPIs right */}
                            <div className="flex items-center gap-3 min-w-0">
                                {/* Avatar */}
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                    {loadingUser ? '…' : initials}
                                </div>

                                {/* Name + pills */}
                                {!loadingUser && user && (
                                    <>
                                        <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
                                            <span className="font-bold text-gray-900 text-sm whitespace-nowrap">{fullName}</span>
                                            {user.active && <Pill label="Active" color="bg-emerald-100 text-emerald-700 border-emerald-200" />}
                                            {user.userType === 'business' && <Pill label="Business" color="bg-blue-100 text-blue-700 border-blue-200" />}
                                            {user.isAgent === 1 && <Pill label="Agent" color="bg-violet-100 text-violet-700 border-violet-200" />}
                                            {user.codEnabled && <Pill label="COD" color="bg-amber-100 text-amber-700 border-amber-200" />}
                                            {loadingPriority && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-gray-100 text-gray-400 border-gray-200 animate-pulse">⭐ …</span>}
                                            {!loadingPriority && priorityStatus === 0 && <Pill label="⭐ P0" color="bg-red-100 text-red-700 border-red-300" />}
                                            {!loadingPriority && priorityStatus === 1 && <Pill label="🌟 P1" color="bg-orange-100 text-orange-700 border-orange-300" />}
                                        </div>

                                        {/* Push KPIs to the right */}
                                        <div className="flex-1" />

                                        {/* KPI stats — right side, no-shrink */}
                                        <div className="flex items-center gap-4 flex-shrink-0 text-xs text-gray-500 whitespace-nowrap">
                                            <span>Orders <span className="font-bold text-gray-800 ml-0.5">{loadingOrders ? '…' : orders.length}</span></span>
                                            <span className="text-gray-200">|</span>
                                            <span>Spend <span className="font-bold text-gray-800 ml-0.5">{loadingOrders ? '…' : `₹${totalSpend.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}</span></span>
                                            <span className="text-gray-200">|</span>
                                            <span>AOV <span className="font-bold text-gray-800 ml-0.5">{loadingOrders ? '…' : (deliveredCount > 0 ? `₹${aov.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—')}</span></span>
                                            {lastOrder && (
                                                <>
                                                    <span className="text-gray-200">|</span>
                                                    <span>Last <span className="font-bold text-gray-800 ml-0.5">{lastOrder.order_date}</span> <span className={`font-semibold ml-1 ${getStatusStyle(lastOrder.status).split(' ')[1]}`}>{lastOrder.status}</span></span>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                                {loadingUser && <span className="text-gray-400 text-sm">Loading…</span>}
                                {userError && <span className="text-red-500 text-sm">{userError}</span>}
                            </div>

                            {/* Row 2: ID · email · phone · source */}
                            {!loadingUser && user && (
                                <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-400 flex-wrap">
                                    <span className="font-mono text-gray-600 font-semibold">#{user.id}</span>
                                    <span className="text-gray-200">·</span>
                                    <span className="truncate max-w-[200px] text-gray-500">{user.email || '—'}{user.emailVerified === 1 && <span className="text-emerald-500 ml-0.5">✓</span>}</span>
                                    <span className="text-gray-200">·</span>
                                    {user.phone ? (
                                        <a
                                            href={`/crm/interaction-timeline?phone_number=${user.phone.replace(/^\+91/, '')}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-medium text-blue-600 hover:underline cursor-pointer"
                                        >
                                            {user.phone}{user.phoneVerified === 1 && <span className="text-emerald-500 ml-0.5">✓</span>}
                                        </a>
                                    ) : (
                                        <span className="font-medium text-amber-500">⚠ No phone</span>
                                    )}
                                    <span className="text-gray-200">·</span>
                                    <span>{user.signupSource}</span>
                                </div>
                            )}
                        </div>

                        {/* Phone missing warning — compact inline banner */}
                        {user && !user.phone && (
                            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm">
                                <span className="text-amber-500">⚠️</span>
                                <span className="font-semibold text-amber-800">Phone missing</span>
                                <span className="text-amber-600 text-xs">— IVR &amp; AI Voice history unavailable.</span>
                            </div>
                        )}

                        {/* Main content grid — tabs left, sidebar right */}
                        <div className="grid grid-cols-3 gap-5">
                            <div className="col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

                                {/* 4-tab navigation bar */}
                                <div className="border-b border-gray-100 px-6 flex overflow-x-auto">
                                    {tabs.map(tab => (
                                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                            className={`py-4 px-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.key
                                                ? 'border-blue-500 text-blue-600'
                                                : 'border-transparent text-gray-400 hover:text-gray-700'}`}>
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Orders list with expandable product rows */}
                                {activeTab === 'orders' && (
                                    <div className="divide-y divide-gray-50">
                                        {loadingOrders && <div className="p-10 text-center text-gray-400 text-sm">Loading orders…</div>}
                                        {!loadingOrders && orders.length === 0 && <div className="p-10 text-center text-gray-300 text-sm">No orders found.</div>}
                                        {/* Status summary strip — grouped counts from actual API values */}
                                        {!loadingOrders && orders.length > 0 && (() => {
                                            // Color map for known display-group statuses returned by the API
                                            const COLOR: Record<string, { bg: string; text: string }> = {
                                                'ACCEPTED': { bg: 'bg-gray-100', text: 'text-gray-700' },
                                                'AWAITING PAYMENT CONFIRMATION': { bg: 'bg-yellow-100', text: 'text-yellow-700' },
                                                'PAYMENT FAILED': { bg: 'bg-orange-100', text: 'text-orange-700' },
                                                'PROCESSING': { bg: 'bg-indigo-100', text: 'text-indigo-700' },
                                                'PACKED': { bg: 'bg-teal-100', text: 'text-teal-700' },
                                                'SHIPPED': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
                                                'DELIVERED': { bg: 'bg-green-100', text: 'text-green-700' },
                                                'RETURN REJECTED': { bg: 'bg-orange-100', text: 'text-orange-600' },
                                                'EXCHANGE REQUESTED': { bg: 'bg-amber-100', text: 'text-amber-700' },
                                                'EXCHANGE APPROVED': { bg: 'bg-amber-200', text: 'text-amber-800' },
                                                'EXCHANGE PICKED': { bg: 'bg-pink-100', text: 'text-pink-700' },
                                                'RETURN REQUESTED': { bg: 'bg-orange-100', text: 'text-orange-700' },
                                                'RETURN APPROVED': { bg: 'bg-rose-100', text: 'text-rose-700' },
                                                'RETURN PICKED': { bg: 'bg-pink-200', text: 'text-pink-800' },
                                                'RETURN DONE': { bg: 'bg-red-100', text: 'text-red-700' },
                                                'CANCELLED': { bg: 'bg-red-100', text: 'text-red-700' },
                                            };
                                            const DEFAULT_COLOR = { bg: 'bg-gray-100', text: 'text-gray-500' };
                                            // Count occurrences per status
                                            const countMap: Record<string, number> = {};
                                            orders.forEach(o => {
                                                const k = (o.status || 'N/A').toUpperCase();
                                                countMap[k] = (countMap[k] || 0) + 1;
                                            });
                                            const entries = Object.entries(countMap).sort((a, b) => b[1] - a[1]);
                                            return (
                                                <div className="flex flex-wrap gap-1.5 px-6 py-2.5 border-b border-gray-100 bg-gray-50/60">
                                                    {entries.map(([status, count]) => {
                                                        const c = COLOR[status] || DEFAULT_COLOR;
                                                        return (
                                                            <span key={status} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${c.bg} ${c.text}`}>
                                                                {status} <span className="font-bold">· {count}</span>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}

                                        {!loadingOrders && orders.map(order => {
                                            const isExpanded = expandedOrder === order.order_id;
                                            return (
                                                <div key={order.order_id} className="hover:bg-gray-50 transition-colors">
                                                    <button className="w-full text-left px-6 py-4 flex items-center gap-4"
                                                        onClick={() => setExpandedOrder(isExpanded ? null : order.order_id)}>
                                                        <div className="w-12 h-12 rounded-lg border border-gray-100 bg-gray-50 overflow-hidden flex-shrink-0">
                                                            {order.items[0]?.product_image_url?.trim() ? (
                                                                <img src={order.items[0].product_image_url.trim()} alt="" className="w-full h-full object-cover"
                                                                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                            ) : <div className="w-full h-full flex items-center justify-center text-gray-300 text-xl">📦</div>}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="font-bold text-gray-900 font-mono text-sm">#{order.order_id}</span>
                                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase ${getStatusStyle(order.status)}`}>{order.status || 'N/A'}</span>
                                                                {order.items.length > 1 && <span className="text-[10px] text-gray-400">+{order.items.length - 1} more</span>}
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-0.5 truncate">{order.items[0]?.product_name}</p>
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            <p className="font-bold text-gray-900">₹{Number(order.order_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                                                            <p className="text-xs text-gray-400">{order.order_date}</p>
                                                        </div>
                                                        <span className="text-gray-300 text-xs ml-1">▼</span>
                                                    </button>
                                                    {isExpanded && (
                                                        <div className="px-6 pb-4 space-y-2">
                                                            {order.items.map(item => (
                                                                <div key={item.item_id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100">
                                                                    <div className="w-10 h-10 rounded-lg border border-gray-200 bg-white overflow-hidden flex-shrink-0">
                                                                        {item.product_image_url?.trim() ? (
                                                                            <img src={item.product_image_url.trim()} alt="" className="w-full h-full object-cover"
                                                                                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                                        ) : <div className="w-full h-full flex items-center justify-center text-xs">📦</div>}
                                                                    </div>
                                                                    <p className="flex-1 text-xs text-gray-700 font-medium leading-tight">{item.product_name}</p>
                                                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase ${getStatusStyle(item.status)}`}>{item.status || '—'}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Tickets tab content */}
                                {activeTab === 'tickets' && (
                                    <div className="divide-y divide-gray-50">
                                        {loadingTickets && <div className="p-10 text-center text-gray-400 text-sm">Loading tickets…</div>}
                                        {!loadingTickets && tickets.length === 0 && <div className="p-10 text-center text-gray-300 text-sm">No tickets found.</div>}
                                        {!loadingTickets && tickets.map((t: any) => {
                                            const st = ticketStatus(t.status);
                                            const pr = ticketPriority(t.priority);
                                            const agentName = t.custom_fields?.cf_agent_name;
                                            const concern = t.custom_fields?.cf_concern_1;
                                            const subConcern = t.custom_fields?.cf_sub_concern_1;
                                            const createdAt = t.created_at ? new Date(t.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '';
                                            return (
                                                <div key={t.id} onClick={() => navigate(`/crm/freshdesk-tickets/${t.id}`)}
                                                    className="px-5 py-3.5 flex items-start gap-3 hover:bg-gray-50 cursor-pointer transition-colors">
                                                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0 mt-0.5">{sourceIcon(t.source)}</div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-mono text-[11px] text-gray-400">#{t.id}</span>
                                                            <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase ${st.color}`}>{st.label}</span>
                                                            {agentName && <span className="text-[10px] text-gray-400">👤 {agentName}</span>}
                                                        </div>
                                                        <p className="font-semibold text-gray-800 text-sm mt-0.5 truncate">{t.subject}</p>
                                                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                                            {concern && <span className="text-[10px] text-gray-400">🏷 {concern}{subConcern ? ` › ${subConcern}` : ''}</span>}
                                                            <span className={`text-[10px] font-semibold ${pr.color}`}>{pr.label}</span>
                                                            <span className="text-[10px] text-indigo-500 font-medium">{createdAt}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* IVR Ozonetel call history tab */}
                                {activeTab === 'ivr' && (
                                    <div className="divide-y divide-gray-50">
                                        {/* Warning shown when customer has no phone — API is skipped */}
                                        {user && !user.phone && (
                                            <div className="m-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                                                <span className="text-amber-500 text-lg flex-shrink-0">⚠️</span>
                                                <div>
                                                    <p className="text-sm font-semibold text-amber-800">Phone number not available</p>
                                                    <p className="text-xs text-amber-600 mt-0.5">IVR call history requires a phone number. This customer does not have one on record.</p>
                                                </div>
                                            </div>
                                        )}
                                        {loadingIvr && <div className="p-10 text-center text-gray-400 text-sm">Loading IVR calls…</div>}
                                        {!loadingIvr && user?.phone && ivrCalls.length === 0 && <div className="p-10 text-center text-gray-300 text-sm">No IVR calls found.</div>}
                                        {!loadingIvr && ivrCalls.map((call: any) => {
                                            const isAnswered = (call.AgentStatus || '').toLowerCase() === 'answered';
                                            const isNotAnswered = (call.AgentStatus || '').toLowerCase().includes('not');
                                            const startDate = call.StartTime ? new Date(call.StartTime) : null;
                                            return (
                                                <div key={call._id || call.UCID} className="px-5 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                                                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 ${isAnswered ? 'bg-emerald-100' : isNotAnswered ? 'bg-red-100' : 'bg-gray-100'}`}>
                                                        {isAnswered ? '📞' : isNotAnswered ? '📵' : '📱'}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-mono text-[11px] text-gray-400">{call.UCID}</span>
                                                            <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase ${isAnswered ? 'bg-emerald-100 text-emerald-700' : isNotAnswered ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                                                                {call.AgentStatus || 'Unknown'}
                                                            </span>
                                                            {call.Agent && <span className="text-[10px] text-gray-400">👤 {call.Agent}</span>}
                                                        </div>
                                                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                                            {call.CampaignName && <span className="text-[10px] text-gray-500">📣 {call.CampaignName}</span>}
                                                            {call.CallDuration && <span className="text-[10px] font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 text-gray-600">⏱ {call.CallDuration}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        {startDate && (
                                                            <>
                                                                <p className="text-xs font-semibold text-gray-700">{startDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                                                <p className="text-[10px] text-gray-400">{startDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                                                            </>
                                                        )}
                                                    </div>
                                                    {call.AudioFile ? (
                                                        <button onClick={() => window.open(call.AudioFile, '_blank')}
                                                            className="w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0 transition-colors">▶</button>
                                                    ) : <div className="w-8 h-8 flex-shrink-0" />}
                                                </div>
                                            );
                                        })}
                                        {!loadingIvr && ivrTotal > PER_PAGE && (
                                            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-50">
                                                <span className="text-[11px] text-gray-400">{ivrTotal} total</span>
                                                <div className="flex gap-2">
                                                    <button disabled={ivrPage <= 1} onClick={() => setIvrPage(p => p - 1)} className="px-3 py-1 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">← Prev</button>
                                                    <span className="px-2 py-1 text-xs text-gray-500">Page {ivrPage}</span>
                                                    <button disabled={ivrPage * PER_PAGE >= ivrTotal} onClick={() => setIvrPage(p => p + 1)} className="px-3 py-1 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next →</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* AI Voice Agent calls from Sahara API */}
                                {activeTab === 'ai' && (
                                    <div className="divide-y divide-gray-50">
                                        {/* Warning shown when customer has no phone — API is skipped */}
                                        {user && !user.phone && (
                                            <div className="m-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                                                <span className="text-amber-500 text-lg flex-shrink-0">⚠️</span>
                                                <div>
                                                    <p className="text-sm font-semibold text-amber-800">Phone number not available</p>
                                                    <p className="text-xs text-amber-600 mt-0.5">AI Voice call history requires a phone number. This customer does not have one on record.</p>
                                                </div>
                                            </div>
                                        )}
                                        {loadingAi && <div className="p-10 text-center text-gray-400 text-sm">Loading AI Voice calls…</div>}
                                        {!loadingAi && user?.phone && aiCalls.length === 0 && <div className="p-10 text-center text-gray-300 text-sm">No AI Voice calls found.</div>}
                                        {!loadingAi && aiCalls.map((call: any) => {
                                            const startDate = call.start_time ? new Date(call.start_time) : null;
                                            const endDate = call.end_time ? new Date(call.end_time) : null;
                                            const durationSec = call.metrics?.total_duration_sec;
                                            const turnCount = call.metrics?.turn_count ?? 0;
                                            const toolCount = call.metrics?.tool_use_count ?? 0;
                                            return (
                                                <div key={call._id || call.ucid} className="px-5 py-4 flex items-start gap-4 hover:bg-gray-50 transition-colors">
                                                    <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center text-base flex-shrink-0 mt-0.5">🤖</div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-mono text-[11px] text-gray-400">{call.ucid}</span>
                                                            <span className="text-[10px] text-gray-400">📞 {call.caller_phone}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            {durationSec != null && <span className="text-[10px] font-mono bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-100">⏱ {formatDuration(durationSec)}</span>}
                                                            <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded border border-blue-100">💬 {turnCount} turns</span>
                                                            {toolCount > 0 && <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded border border-amber-100">⚡ {toolCount} tools</span>}
                                                        </div>
                                                        {startDate && (
                                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                                {startDate.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                                {endDate ? ` → ${endDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {call.recording_url ? (
                                                        <button onClick={() => window.open(call.recording_url, '_blank')}
                                                            className="w-8 h-8 rounded-full bg-purple-50 hover:bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0 transition-colors mt-0.5">▶</button>
                                                    ) : <div className="w-8 h-8 flex-shrink-0" />}
                                                </div>
                                            );
                                        })}
                                        {!loadingAi && aiTotal > PER_PAGE && (
                                            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-50">
                                                <span className="text-[11px] text-gray-400">{aiTotal} total</span>
                                                <div className="flex gap-2">
                                                    <button disabled={aiPage <= 1} onClick={() => setAiPage(p => p - 1)} className="px-3 py-1 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">← Prev</button>
                                                    <span className="px-2 py-1 text-xs text-gray-500">Page {aiPage}</span>
                                                    <button disabled={aiPage * PER_PAGE >= aiTotal} onClick={() => setAiPage(p => p + 1)} className="px-3 py-1 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next →</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Right sidebar - AI Insights, Quick Actions, Order Summary */}
                            <div className="space-y-4">
                                {/* <div className="bg-slate-900 rounded-2xl p-5 text-white relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-6 opacity-5">
                                        <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2L2 7l10 5 10-5-10-5zm0 9l2.5-1.25L12 8.5l-2.5 1.25L12 11zm0 2.5l-5-2.5-5 2.5L12 22l10-8.5-5-2.5-5 2.5z" />
                                        </svg>
                                    </div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="text-base">✨</span>
                                        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-300">AI Insights</h3>
                                    </div>
                                    <div className="space-y-2.5">
                                        {['High frequency in Safety Gear category during winter.', 'Typically orders via Mobile App during peak hours.', 'Probability of churn is Very Low (2.4%).'].map((insight, i) => (
                                            <div key={i} className="flex items-start gap-2.5">
                                                <div className="mt-0.5 w-4 h-4 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-blue-400 text-[9px]">✓</span>
                                                </div>
                                                <p className="text-gray-300 text-xs leading-relaxed">{insight}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div> */}
                                {/* Agent Notes panel replaces the static Quick Actions card */}
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Agent Notes</h3>

                                    {/* New note input and save button */}
                                    <div className="mb-4">
                                        <textarea
                                            value={newNote}
                                            onChange={e => setNewNote(e.target.value)}
                                            placeholder="Add a note about this customer…"
                                            rows={3}
                                            className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all placeholder-gray-300"
                                        />
                                        <button
                                            onClick={handleCreateNote}
                                            disabled={savingNote || !newNote.trim()}
                                            className="mt-2 w-full py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                                        >
                                            {savingNote ? (
                                                // Inline spinner while saving
                                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                </svg>
                                            ) : '💾'} Save Note
                                        </button>
                                    </div>

                                    {/* Existing notes list — sorted by creation time descending */}
                                    <div className="space-y-2 max-h-72 overflow-y-auto">
                                        {loadingNotes && (
                                            <p className="text-xs text-gray-400 text-center py-3">Loading notes…</p>
                                        )}
                                        {!loadingNotes && notes.length === 0 && (
                                            <p className="text-xs text-gray-300 text-center py-3">No notes yet.</p>
                                        )}
                                        {!loadingNotes && notes.map((n: any) => {
                                            // Always stringify noteId — MongoDB ObjectId can be an object
                                            const noteId = String(n._id || n.id || n.note_id || '');
                                            const isEditing = editingNoteId === noteId;
                                            const isUpdating = updatingNoteId === noteId;
                                            return (
                                                <div key={noteId} className="group bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 relative">
                                                    {isEditing ? (
                                                        // Inline edit mode — textarea + confirm/cancel buttons
                                                        <>
                                                            <textarea
                                                                value={editingNoteText}
                                                                onChange={e => setEditingNoteText(e.target.value)}
                                                                rows={3}
                                                                autoFocus
                                                                className="w-full text-xs border border-amber-300 rounded-lg px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
                                                            />
                                                            <div className="flex gap-1.5 mt-1.5">
                                                                <button
                                                                    onClick={() => handleUpdateNote(noteId)}
                                                                    disabled={isUpdating}
                                                                    className="flex-1 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
                                                                >
                                                                    {isUpdating ? (
                                                                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                                                                    ) : '✓'} Save
                                                                </button>
                                                                <button
                                                                    onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                                                                    className="flex-1 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 text-[11px] font-semibold"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        // Read mode — note text with edit button on hover
                                                        <>
                                                            <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{n.note}</p>
                                                            <div className="flex items-center justify-between mt-1.5">
                                                                <span className="text-[10px] text-gray-400">{n.agent_email || n.editor_email || ''}</span>
                                                                <span className="text-[10px] text-gray-300">{n.timestamp ? new Date(n.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                                            </div>
                                                            <button
                                                                onClick={() => { setEditingNoteId(noteId); setEditingNoteText(n.note); }}
                                                                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-blue-500 hover:text-blue-700 bg-white border border-blue-100 rounded-md px-1.5 py-0.5"
                                                            >
                                                                Edit
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {!loadingOrders && orders.length > 0 && (() => {
                                    const s = (o: any) => (o.status || '').toUpperCase();
                                    const groups = [
                                        { label: 'Accepted', color: 'text-gray-600', count: orders.filter(o => ['NEW', 'ASSIST_CONVERT', 'VERIFICATION_PENDING', 'CUSTOMER_EXCHANGE_VERIFIED', 'VERIFIED'].includes(s(o))).length },
                                        { label: 'Awaiting Payment', color: 'text-yellow-600', count: orders.filter(o => ['PAYMENT_PENDING', 'PAYMENT_GATEWAY_PENDING'].includes(s(o))).length },
                                        { label: 'Payment Failed', color: 'text-orange-600', count: orders.filter(o => s(o) === 'PAYMENT_FAILED').length },
                                        { label: 'Processing', color: 'text-indigo-600', count: orders.filter(o => ['SUPPLIER_ASSIGNED', 'FAILED_SUPPLIER', 'PICKUP_SCHEDULED', 'LOGISTIC_PICKUP_ACTIVATE', 'OUT_FOR_PICKUP', 'PICKUP_PENDING', 'PICKUP_DONE', 'INVENTORY_PICKUP', 'SUPPLIER_EXCHANGE_REQUESTED', 'SUPPLIER_OUT_FOR_EXCHANGE', 'QC_FAIL', 'QC_PASS', 'DROPSHIP_DETECTED', 'DROPSHIP_ACTIVATED', 'SHIPMENT_CREATED'].includes(s(o))).length },
                                        { label: 'Packed', color: 'text-teal-600', count: orders.filter(o => ['PACKED', 'MANIFEST_CREATED'].includes(s(o))).length },
                                        { label: 'Shipped', color: 'text-emerald-600', count: orders.filter(o => s(o) === 'SHIPPED').length },
                                        { label: 'Delivered', color: 'text-green-600', count: orders.filter(o => ['DELIVERED', 'CLOSED'].includes(s(o))).length },
                                        { label: 'Return Rejected', color: 'text-orange-500', count: orders.filter(o => ['RETURN_NOT_REQUIRED', 'REQUEST_REJECTED'].includes(s(o))).length },
                                        { label: 'Exchange Requested', color: 'text-amber-600', count: orders.filter(o => s(o) === 'CUSTOMER_EXCHANGE_REQUESTED').length },
                                        { label: 'Exchange Approved', color: 'text-amber-700', count: orders.filter(o => ['RVP_RAISED', 'EXCHANGE_APPROVED', 'CUSTOMER_EXCHANGE_APPROVED'].includes(s(o))).length },
                                        { label: 'Exchange Picked', color: 'text-pink-600', count: orders.filter(o => ['RETURN_PICKED', 'WAREHOUSE_RETURN', 'RTV_DONE', 'RTV_DENIED', 'RTV_INITIATED', 'RETURN_QCPASS', 'RETURN_QCFAIL', 'TAX_PO', 'INVENTORIZE'].includes(s(o))).length },
                                        { label: 'Return Requested', color: 'text-orange-600', count: orders.filter(o => s(o) === 'RETURN_REQUESTED').length },
                                        { label: 'Return Approved', color: 'text-rose-600', count: orders.filter(o => ['RVP_RAISED', 'RETURN_APPROVED'].includes(s(o))).length },
                                        { label: 'Return Done', color: 'text-red-600', count: orders.filter(o => ['WAREHOUSE_RETURN', 'RTV_DONE', 'RTV_DENIED', 'RTV_INITIATED', 'RETURN_QCPASS', 'RETURN_QCFAIL', 'TAX_PO', 'INVENTORIZE'].includes(s(o))).length },
                                        { label: 'Cancelled', color: 'text-red-500', count: orders.filter(o => ['RETURN_TO_ORIGIN', 'CUSTOMER_CANCEL', 'ADMIN_CANCEL', 'DUMMY_ORDER'].includes(s(o))).length },
                                    ].filter(g => g.count > 0);
                                    if (groups.length === 0) return null;
                                    return (
                                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Order Summary</h3>
                                            <div className="space-y-1.5">
                                                {groups.map(row => (
                                                    <div key={row.label} className="flex items-center justify-between text-sm">
                                                        <span className="text-gray-500">{row.label}</span>
                                                        <span className={`font-bold ${row.color}`}>{row.count}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}

                            </div>
                        </div>
                    </>
                )}
            </div>
        </StitchLayout>
    );
};

export default CustomerProfile;
